import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    ensureSocketConnected,
    getSocket,
    isSocketConnected,
} from '../services/socket';
import { setConnected } from '../redux/reducers/chatRealtime';

/**
 * Mantiene una conexión de socket a nivel de aplicación mientras el usuario
 * está autenticado. Refleja el estado de conexión en `chatRealtime.connected`.
 * Tras idle/background, reintenta reconectar al volver a la pestaña o interactuar.
 */
export const usePresenceSocket = () => {
    const dispatch = useDispatch();
    const userId = useSelector((state) => state.user.user?._id);

    useEffect(() => {
        if (!userId) return undefined;

        let mounted = true;
        let detach = null;
        let resumeInFlight = false;
        let retryTimer = null;

        const markConnected = (value) => {
            if (mounted) dispatch(setConnected(Boolean(value)));
        };

        const clearRetry = () => {
            if (retryTimer) {
                clearInterval(retryTimer);
                retryTimer = null;
            }
        };

        const scheduleRetryWhileOffline = () => {
            if (retryTimer || !mounted) return;
            retryTimer = setInterval(() => {
                if (!mounted) {
                    clearRetry();
                    return;
                }
                if (isSocketConnected()) {
                    markConnected(true);
                    clearRetry();
                    return;
                }
                syncPresence({ force: true });
            }, 20_000);
        };

        const bindLifecycle = (socket) => {
            if (!socket || detach) return;

            const onConnect = () => {
                markConnected(true);
                clearRetry();
            };
            const onDisconnect = () => {
                markConnected(false);
                scheduleRetryWhileOffline();
            };

            socket.on('connect', onConnect);
            socket.on('disconnect', onDisconnect);

            detach = () => {
                try {
                    socket?.off?.('connect', onConnect);
                    socket?.off?.('disconnect', onDisconnect);
                } catch {
                    // el socket ya se cerró
                }
                detach = null;
            };
        };

        const syncPresence = async ({ force = false } = {}) => {
            if (!mounted || resumeInFlight) return;

            if (!force && isSocketConnected()) {
                bindLifecycle(getSocket());
                markConnected(true);
                return;
            }

            resumeInFlight = true;
            try {
                const socket = await ensureSocketConnected();
                if (!mounted) return;
                bindLifecycle(socket);
                markConnected(Boolean(socket?.connected));
                if (socket?.connected) clearRetry();
                else scheduleRetryWhileOffline();
            } catch {
                if (mounted && !isSocketConnected()) {
                    markConnected(false);
                    scheduleRetryWhileOffline();
                }
            } finally {
                resumeInFlight = false;
            }
        };

        syncPresence({ force: true });

        const onResume = () => {
            if (document.visibilityState === 'hidden') return;
            syncPresence({ force: !isSocketConnected() });
        };

        const onInteract = () => {
            if (document.visibilityState === 'hidden') return;
            if (isSocketConnected()) {
                bindLifecycle(getSocket());
                markConnected(true);
                return;
            }
            syncPresence({ force: true });
        };

        document.addEventListener('visibilitychange', onResume);
        window.addEventListener('focus', onResume);
        window.addEventListener('online', onResume);
        window.addEventListener('pointerdown', onInteract, { passive: true });
        window.addEventListener('keydown', onInteract);

        return () => {
            mounted = false;
            clearRetry();
            document.removeEventListener('visibilitychange', onResume);
            window.removeEventListener('focus', onResume);
            window.removeEventListener('online', onResume);
            window.removeEventListener('pointerdown', onInteract);
            window.removeEventListener('keydown', onInteract);
            if (detach) detach();
        };
    }, [userId, dispatch]);
};
